/**
 * New joiners, waiting to be tagged.
 *
 * Route: /my-workspace/new-users
 *
 * WHY THIS SCREEN EXISTS
 *
 * A person created in the PHP portal arrives here carrying employment_status = 0,
 * which is PHP's way of saying "nobody has decided yet". It is not one of the four
 * real statuses.
 *
 * The All Users grid filters on employment_status IN (1, 4) and joins
 * employment_statuses with an INNER JOIN. A person carrying 0 fails both, so they
 * appear on NO screen in this portal and HR has no way to reach them. They are in
 * the database and invisible.
 *
 * This is the list that reaches them. Tagging somebody sets a real status, so they
 * drop off this list by themselves and turn up in All Users instead. There is
 * nothing to tick off and no way for the two lists to disagree.
 *
 * See the node portal repo, docs/user-role-tag-with-history/README.md
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';

import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import Loader3D from '../../../../components/Loader/Loader3D';
import apiRequest from '../../../../api/request';

/** dd/mm/yyyy for reading. The API speaks yyyy-mm-dd. */
const showDate = (value) => {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

export default function NewJoinees() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');

  /**
   * `silent` fetches fresh data without putting the page back into its loading
   * state, for a refresh that happens while something is already on screen.
   */
  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const res = await apiRequest({ method: 'get', url: 'users/new-joinees' });
    if (res?.status === 'success') {
      setRows(res.data?.rows || []);
      setError('');
    } else {
      setError(res?.message || 'Could not load the new joiners');
    }
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Small list by design, so the filter is done here rather than as another call.
  const term = search.trim().toLowerCase();
  const visible = term
    ? rows.filter((r) =>
        [r.name, r.email, r.phone, r.empId, String(r.userId)]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term)),
      )
    : rows;

  const pageActions = (
    <Stack direction="row" spacing={1}>
      <Button variant="outlined" size="small" onClick={() => load()}
        startIcon={<Icon icon="mdi:refresh" />}>
        Refresh
      </Button>
      <Button variant="outlined" size="small" onClick={() => navigate('/my-workspace/all-users')}
        startIcon={<Icon icon="mdi:account-group-outline" />}>
        All Users
      </Button>
    </Stack>
  );

  if (error) {
    return (
      <GlobalWrapper title="New Joiners" actions={pageActions} skelton={false}>
        <Alert severity="error">{error}</Alert>
      </GlobalWrapper>
    );
  }

  // GlobalWrapper's own skeleton stops after a second and leaves an empty page
  // behind, so this loader is ours and stays until there is something to show.
  if (loading) {
    return (
      <GlobalWrapper title="New Joiners" actions={pageActions} skelton={false}>
        <Loader3D />
      </GlobalWrapper>
    );
  }

  return (
    <GlobalWrapper title="New Joiners" actions={pageActions} skelton={false}>
      <Stack spacing={2}>
        <Alert severity="info" icon={<Icon icon="mdi:information-outline" />}>
          <Typography variant="subtitle2">
            {rows.length === 0
              ? 'Nobody is waiting to be tagged'
              : `${rows.length} ${rows.length === 1 ? 'person is' : 'people are'} waiting to be tagged`}
          </Typography>
          <Typography variant="body2">
            These people were created in the other portal and nobody has given them a
            role yet, so they do not appear in All Users. Tagging somebody moves them
            into All Users straight away, and they leave this list.
          </Typography>
        </Alert>

        {rows.length > 0 && (
          <Paper variant="outlined">
            <Box sx={{ p: 2, pb: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between"
                spacing={2} flexWrap="wrap" useFlexGap>
                <Box>
                  <Typography variant="subtitle2">Waiting to be tagged</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Newest first, by the date they were created
                  </Typography>
                </Box>
                <TextField size="small" placeholder="Search name, email, phone or ID"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  sx={{ minWidth: 260 }}
                  InputProps={{
                    startAdornment: (
                      <Icon icon="mdi:magnify" style={{ marginRight: 6, opacity: 0.6 }} />
                    ),
                  }} />
              </Stack>
            </Box>
            <Divider />
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Date of Joining</TableCell>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>BGV Verification</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Login</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((r) => (
                    <TableRow key={r.userId} hover>
                      <TableCell>
                        {r.name ? (
                          <Typography variant="body2">{r.name}</Typography>
                        ) : (
                          <Tooltip title="No name on the record yet, so their profile is not finished">
                            <Chip size="small" color="warning" variant="outlined"
                              label="No name yet" />
                          </Tooltip>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          ID {r.userId}
                        </Typography>
                      </TableCell>
                      <TableCell>{r.email || ''}</TableCell>
                      <TableCell>{r.phone || ''}</TableCell>
                      <TableCell>{showDate(r.joinedOn)}</TableCell>
                      <TableCell>
                        {r.empId ? String(r.empId).toUpperCase() : ''}
                      </TableCell>
                      {/* documents_verification_status is the BGV field. Only the
                          value 2 counts as approved. Anything else, including an
                          empty one, means it has not been approved yet. */}
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={Number(r.documentsStatus) === 2 ? 'success' : 'warning'}
                          label={
                            Number(r.documentsStatus) === 2 ? 'Approved' : 'Not approved'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {r.hasRole ? (
                          r.roleName || ''
                        ) : (
                          <Chip size="small" variant="outlined" label="Not tagged" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined"
                          color={r.loginStatus === 1 ? 'success' : 'default'}
                          label={r.loginStatus === 1 ? 'Active' : 'Inactive'} />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="contained"
                          onClick={() =>
                            navigate(`/my-workspace/employee-tagging/${r.userId}`)}
                          startIcon={<Icon icon="mdi:tag-outline" />}>
                          Role Tag
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {visible.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography variant="body2" color="text.secondary">
                          Nobody matches that search.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>
    </GlobalWrapper>
  );
}
